import { ModuleHeader } from "./shell/module-header";
import { useState, useMemo, useEffect } from "react";
import {
  Zap, X, ArrowLeft, CheckCircle2, Loader2, AlertTriangle, Plug, Lock, Sparkles,
} from "lucide-react";
import { AIAssistantSidebar } from "./ai-assistant-sidebar";
import { useIntegrations } from "../hooks/common/useIntegrations";
import {
  INTEGRATION_CATEGORIES,
  INTEGRATION_STATUS_CONFIG,
} from "../data/integrations-data";
import type { Integration } from "../services/common/integrations-service";
import {
  publishingApi,
  type Integration as PubIntegration,
} from "../services/publishing/publishing-api";
import { PublishingIntegrationStatus } from "./publishing/publishing-integration-status";
import { GoogleConnectorStatus } from "./google/google-connector-status";
import {
  googleConnectorsApi,
  type GoogleProvider,
  type GoogleProviderStatus,
} from "../services/analytics/google-connectors-api";
import { Button, SearchInput } from "./kit";

interface IntegrationsPageProps {
  isSidebarCollapsed?: boolean;
  onSidebarToggle?: (collapsed: boolean) => void;
  /** Active brand — publishing destinations are scoped per-brand. */
  activeBrandName?: string;
}

// ─── Publishing-card status (connection-state, not roadmap) ──────────────────

type PublishingCardStatus = "ready" | "configure";

const PUBLISHING_STATUS_CONFIG: Record<
  PublishingCardStatus,
  {
    label: string;
    color: string;
    bgColor: string;
    borderColor: string;
    icon: React.ReactNode;
  }
> = {
  ready: {
    label: "Ready",
    color: "var(--success-text)",
    bgColor: "var(--success-bg)",
    borderColor: "var(--success-border)",
    icon: <CheckCircle2 className="w-3.5 h-3.5" />,
  },
  configure: {
    label: "Configure",
    color: "var(--signal-blue, #2962FF)",
    bgColor: "var(--signal-blue-10, rgba(41,98,255,0.08))",
    borderColor: "var(--signal-blue-30, rgba(41,98,255,0.25))",
    icon: <Plug className="w-3.5 h-3.5" />,
  },
};

// ─── What a read-only Google source never does ───────────────────────────────
// Fixed for every `googleConnector` card: the scope is read-only, so these two
// lines are a property of the connection rather than of the individual card.

const GOOGLE_NEVER_DOES: readonly string[] = [
  "Write to the property.",
  "Read user-level data.",
];

// ═══════════════════════════════════════════════════════════════════════════
//  Main page
// ═══════════════════════════════════════════════════════════════════════════

export function IntegrationsPage({
  isSidebarCollapsed = false,
  onSidebarToggle,
  activeBrandName,
}: IntegrationsPageProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIntegration, setSelectedIntegration] =
    useState<Integration | null>(null);
  const [aiTopic, setAiTopic] = useState<string | undefined>(undefined);

  const { integrations, isLoading, error } = useIntegrations();

  // Live per-brand status for publishing destinations
  const [pubStatus, setPubStatus] = useState<Record<string, PubIntegration>>({});
  const [pubStatusLoading, setPubStatusLoading] = useState(true);
  const [pubStatusTick, setPubStatusTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setPubStatusLoading(true);
    (async () => {
      try {
        const items = await publishingApi.listIntegrations(activeBrandName);
        if (cancelled) return;
        const map: Record<string, PubIntegration> = {};
        for (const item of items) map[item.destination] = item;
        setPubStatus(map);
      } catch {
        if (!cancelled) setPubStatus({});
      } finally {
        if (!cancelled) setPubStatusLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeBrandName, pubStatusTick]);

  // Live per-brand status for the two read-only Google connectors. Fetched on
  // the same tick as the publishing status so closing the detail panel
  // re-segments both families at once.
  const [googleStatus, setGoogleStatus] =
    useState<Partial<Record<GoogleProvider, GoogleProviderStatus>>>({});

  useEffect(() => {
    let cancelled = false;
    if (!activeBrandName) {
      setGoogleStatus({});
      return;
    }
    (async () => {
      try {
        const res = await googleConnectorsApi.status(activeBrandName);
        if (!cancelled) setGoogleStatus(res.providers || {});
      } catch {
        if (!cancelled) setGoogleStatus({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeBrandName, pubStatusTick]);

  const refreshPubStatus = () => setPubStatusTick((t) => t + 1);

  const handleOpenAIChat = (topic?: string) => {
    setAiTopic(topic);
    if (isSidebarCollapsed && onSidebarToggle) {
      onSidebarToggle(false);
    }
  };

  useEffect(() => {
    if (aiTopic) {
      const timer = setTimeout(() => setAiTopic(undefined), 500);
      return () => clearTimeout(timer);
    }
  }, [aiTopic]);

  // ─── Search filter ─────────────────────────────────────────────────────────

  const matchesSearch = (i: Integration): boolean => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      i.name.toLowerCase().includes(q) ||
      i.description.toLowerCase().includes(q)
    );
  };

  // ─── Segment into sections ─────────────────────────────────────────────────

  const { configured, availableToConfigure, comingSoon, totalMatched } =
    useMemo(() => {
      // Two families are connectable now: publishing destinations and the two
      // read-only Google connectors. Everything else is still roadmap.
      const connectable = integrations.filter(
        (i) => i.isPublishingDestination || i.googleConnector,
      );
      const roadmap = integrations.filter(
        (i) => !i.isPublishingDestination && !i.googleConnector,
      );

      const configured: Integration[] = [];
      const availableToConfigure: Integration[] = [];

      for (const i of connectable) {
        let isConnected = false;
        if (i.isPublishingDestination) {
          const dest = i.publishingDestination;
          const live = dest ? pubStatus[dest] : undefined;
          isConnected = !!live && live.status === "active";
        } else if (i.googleConnector) {
          const live = googleStatus[i.googleConnector];
          isConnected = !!live?.connected && live.status === "active";
        }
        if (isConnected) configured.push(i);
        else availableToConfigure.push(i);
      }

      const filteredConfigured = configured.filter(matchesSearch);
      const filteredAvailable = availableToConfigure.filter(matchesSearch);
      const filteredSoon = roadmap.filter(matchesSearch);

      return {
        configured: filteredConfigured,
        availableToConfigure: filteredAvailable,
        comingSoon: filteredSoon,
        totalMatched:
          filteredConfigured.length +
          filteredAvailable.length +
          filteredSoon.length,
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [integrations, pubStatus, googleStatus, searchQuery]);

  const totalAll = integrations.length;

  // ─── States ────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <PageShell
        isSidebarCollapsed={isSidebarCollapsed}
        onSidebarToggle={onSidebarToggle}
        aiTopic={aiTopic}
      >
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <Loader2
              className="w-8 h-8 animate-spin mx-auto mb-4"
              style={{ color: "var(--muted-foreground)" }}
            />
            <p
              style={{
                color: "var(--muted-foreground)",
                fontSize: "var(--text-sm)",
              }}
            >
              Loading integrations…
            </p>
          </div>
        </div>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell
        isSidebarCollapsed={isSidebarCollapsed}
        onSidebarToggle={onSidebarToggle}
        aiTopic={aiTopic}
      >
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <AlertTriangle
              className="w-8 h-8 mx-auto mb-4"
              style={{ color: "var(--infrared-coral, #EF4444)" }}
            />
            <p
              style={{
                color: "var(--foreground)",
                fontSize: "var(--text-base)",
                fontWeight: "var(--font-weight-semibold)",
                marginBottom: 8,
              }}
            >
              Failed to load integrations
            </p>
            <p
              style={{
                color: "var(--muted-foreground)",
                fontSize: "var(--text-sm)",
              }}
            >
              {error.message}
            </p>
          </div>
        </div>
      </PageShell>
    );
  }

  // ─── Main render ───────────────────────────────────────────────────────────

  return (
    <PageShell
      isSidebarCollapsed={isSidebarCollapsed}
      onSidebarToggle={onSidebarToggle}
      aiTopic={aiTopic}
    >
      <ModuleHeader
        page="Integrations"
        icon={Zap}
        compact
        title="Connect your stack. Amplify AI insights everywhere."
        description="Connect publishing destinations to push brand content directly from Upmode, and connect Google Analytics or Search Console to read your own measured demand into the Outcomes view. More integrations are on the way: BI and marketing tools are coming soon."
      />
      <div className="w-full px-4 md:px-8">
        {/* ── Search ── */}
        <div
          className="py-3 md:py-4 border-b"
          style={{ borderColor: "var(--border)" }}
        >
          <div className="max-w-md">
            <SearchInput
              type="search"
              placeholder="Search integrations"
              aria-label="Search integrations"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClear={() => setSearchQuery("")}
            />
          </div>

          {searchQuery.trim() && (
            <p
              className="mt-2"
              style={{
                fontSize: 12,
                color: "var(--muted-foreground)",
              }}
            >
              Showing {totalMatched} of {totalAll} integrations
              {totalMatched === 0 && <>, try a different search term</>}
            </p>
          )}
        </div>

        {/* ── Section 1: Configured for <brand> ── */}
        <Section
          title={
            activeBrandName
              ? `Configured for ${activeBrandName}`
              : "Configured"
          }
          subtitle={
            activeBrandName
              ? `Currently connected for ${activeBrandName}.`
              : "Select a brand to see what is connected."
          }
          emptyMessage={
            pubStatusLoading
              ? ""
              : activeBrandName
                ? "Nothing configured yet for this brand. Pick one from the section below to get started."
                : ""
          }
          count={configured.length}
          loading={pubStatusLoading}
          hideWhenEmpty={!activeBrandName}
        >
          {configured.map((i) => (
            <PublishingCard
              key={i.id}
              integration={i}
              status="ready"
              onClick={() => setSelectedIntegration(i)}
            />
          ))}
        </Section>

        {/* ── Section 2: Available to configure ── */}
        <Section
          title="Available to configure"
          subtitle="Ready to connect. Publishing destinations need credentials from the service; the Google connectors need a service account key that has been granted read access."
          emptyMessage="Everything available has been configured."
          count={availableToConfigure.length}
        >
          {availableToConfigure.map((i) => (
            <PublishingCard
              key={i.id}
              integration={i}
              status="configure"
              onClick={() => setSelectedIntegration(i)}
            />
          ))}
        </Section>

        {/* ── Section 3: Coming soon ── */}
        <Section
          title="Coming soon"
          subtitle="Integrations on our roadmap. Click any card to learn what's planned."
          emptyMessage="No matching upcoming integrations."
          count={comingSoon.length}
          lastSection
        >
          {comingSoon.map((i) => (
            <ComingSoonCard
              key={i.id}
              integration={i}
              onClick={() => setSelectedIntegration(i)}
            />
          ))}
        </Section>
      </div>

      {selectedIntegration && (
        <IntegrationDetailPanel
          integration={selectedIntegration}
          onClose={() => {
            setSelectedIntegration(null);
            // Re-fetch publishing status in case user connected something
            refreshPubStatus();
          }}
          onOpenAIChat={handleOpenAIChat}
          activeBrandName={activeBrandName}
        />
      )}
    </PageShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Page shell (sidebar + main)
// ═══════════════════════════════════════════════════════════════════════════

function PageShell({
  isSidebarCollapsed,
  onSidebarToggle,
  aiTopic,
  children,
}: {
  isSidebarCollapsed?: boolean;
  onSidebarToggle?: (c: boolean) => void;
  aiTopic?: string;
  children: React.ReactNode;
}) {
  // The AIAssistantSidebar is position:fixed, so content will flow under
  // it regardless of margin. We only reserve the *collapsed* 64px handle
  // width so the rightmost content doesn't sit behind the tab handle.
  // When expanded, the sidebar floats over the content like a true flyout —
  // user can collapse it to see content underneath.
  return (
    <div
      className="flex min-h-screen"
      data-kit
      style={{ backgroundColor: "var(--background)" }}
    >
      <div
        className="flex-1 overflow-auto transition-all duration-300"
        style={{ marginRight: 64 }}
      >
        {children}
      </div>

      <AIAssistantSidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={onSidebarToggle}
        initialTopic={aiTopic}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Section
// ═══════════════════════════════════════════════════════════════════════════

function Section({
  title,
  subtitle,
  count,
  loading,
  emptyMessage,
  hideWhenEmpty,
  lastSection,
  children,
}: {
  title: string;
  subtitle: string;
  count: number;
  loading?: boolean;
  emptyMessage?: string;
  hideWhenEmpty?: boolean;
  lastSection?: boolean;
  children: React.ReactNode;
}) {
  if (hideWhenEmpty && count === 0 && !loading) return null;

  return (
    <div
      className={`py-4 md:py-5 ${lastSection ? "" : "border-b"}`}
      style={{ borderColor: "var(--border)" }}
    >
      <div className="mb-3 md:mb-4">
        <div
          className="flex items-baseline gap-2 flex-wrap"
          style={{ marginBottom: 2 }}
        >
          <h2
            style={{
              fontSize: "var(--text-base, 15px)",
              fontWeight: "var(--font-weight-semibold)",
              color: "var(--foreground)",
              lineHeight: 1.3,
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
          >
            {title}
          </h2>
          {count > 0 && (
            <span
              style={{
                fontSize: 11,
                padding: "1px 7px",
                borderRadius: 999,
                backgroundColor: "var(--muted)",
                border: "1px solid var(--border)",
                color: "var(--muted-foreground)",
                fontWeight: "var(--font-weight-semibold)",
                whiteSpace: "nowrap",
              }}
            >
              {count}
            </span>
          )}
        </div>
        <p
          style={{
            fontSize: "var(--text-xs, 12px)",
            color: "var(--muted-foreground)",
            lineHeight: 1.5,
          }}
        >
          {subtitle}
        </p>
      </div>

      {loading ? (
        <div
          className="flex items-center gap-2"
          style={{ color: "var(--muted-foreground)" }}
        >
          <Loader2 className="w-4 h-4 animate-spin" />
          <span style={{ fontSize: "var(--text-sm)" }}>Checking status…</span>
        </div>
      ) : count === 0 ? (
        emptyMessage ? (
          <div
            className="py-4 px-4 rounded-md"
            style={{
              border: "1px dashed var(--border)",
              backgroundColor: "var(--muted)",
              fontSize: "var(--text-xs, 12px)",
              color: "var(--muted-foreground)",
              lineHeight: 1.5,
            }}
          >
            {emptyMessage}
          </div>
        ) : null
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5"
          style={{ alignItems: "start" }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Publishing card (Sections 1 & 2 — interactive)
// ═══════════════════════════════════════════════════════════════════════════

function PublishingCard({
  integration,
  status,
  onClick,
}: {
  integration: Integration;
  status: PublishingCardStatus;
  onClick: () => void;
}) {
  const IconComponent = integration.icon;
  const statusConfig = PUBLISHING_STATUS_CONFIG[status];

  return (
    <div className="upm-card transition-all hover-lift cursor-pointer group" onClick={onClick} style={{
        padding: 12,
        
        borderColor:
          status === "ready" ? "var(--success-border)" : "var(--border)",
        boxShadow: "var(--elevation-sm)",
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}>
      <div className="flex items-center gap-2.5">
        <div
          className="rounded flex items-center justify-center flex-shrink-0"
          style={{
            width: 28,
            height: 28,
            backgroundColor: "var(--muted)",
            border: "1px solid var(--border)",
          }}
        >
          <IconComponent
            style={{ color: "var(--foreground)", width: 15, height: 15 }}
          />
        </div>

        <h3
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 13,
            fontWeight: "var(--font-weight-semibold)",
            color: "var(--foreground)",
            lineHeight: 1.25,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {integration.name}
        </h3>

        <span
          className="flex items-center gap-1 flex-shrink-0"
          style={{
            padding: "2px 6px",
            borderRadius: 999,
            backgroundColor: statusConfig.bgColor,
            color: statusConfig.color,
            fontSize: 9,
            fontWeight: "var(--font-weight-semibold)",
            border: `1px solid ${statusConfig.borderColor}`,
            whiteSpace: "nowrap",
            lineHeight: 1,
            textTransform: "uppercase",
            letterSpacing: "0.03em",
          }}
        >
          {statusConfig.icon}
          {statusConfig.label}
        </span>
      </div>

      <p
        style={{
          fontSize: 11.5,
          color: "var(--muted-foreground)",
          lineHeight: 1.4,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          margin: 0,
        }}
      >
        {integration.description}
      </p>

      <span
        style={{
          fontSize: 11,
          color: "var(--accent)",
          fontWeight: "var(--font-weight-semibold)",
        }}
      >
        {status === "ready" ? "Manage →" : "Configure →"}
      </span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Coming soon card (Section 3 — read-only, greyed)
// ═══════════════════════════════════════════════════════════════════════════

function ComingSoonCard({
  integration,
  onClick,
}: {
  integration: Integration;
  onClick: () => void;
}) {
  const IconComponent = integration.icon;
  const roadmapLabel = {
    live: "Planned",
    "in-progress": "In progress",
    planned: "Planned",
  }[integration.status];

  return (
    <div className="upm-card transition-all cursor-pointer group" onClick={onClick} style={{
        padding: 12,
        backgroundColor: "var(--muted)",
        
        boxShadow: "none",
        opacity: 0.9,
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}>
      <div className="flex items-center gap-2.5">
        <div
          className="rounded flex items-center justify-center flex-shrink-0"
          style={{
            width: 28,
            height: 28,
            backgroundColor: "var(--background)",
            border: "1px solid var(--border)",
            opacity: 0.75,
          }}
        >
          <IconComponent
            style={{ color: "var(--muted-foreground)", width: 15, height: 15 }}
          />
        </div>

        <h3
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 13,
            fontWeight: "var(--font-weight-semibold)",
            color: "var(--foreground)",
            lineHeight: 1.25,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {integration.name}
        </h3>

        <span
          className="flex items-center gap-1 flex-shrink-0"
          style={{
            padding: "2px 6px",
            borderRadius: 999,
            backgroundColor: "var(--background)",
            color: "var(--muted-foreground)",
            fontSize: 9,
            fontWeight: "var(--font-weight-semibold)",
            border: "1px solid var(--border)",
            whiteSpace: "nowrap",
            lineHeight: 1,
            textTransform: "uppercase",
            letterSpacing: "0.03em",
          }}
        >
          <Lock style={{ width: 10, height: 10 }} />
          {roadmapLabel}
        </span>
      </div>

      <p
        style={{
          fontSize: 11.5,
          color: "var(--muted-foreground)",
          lineHeight: 1.4,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          margin: 0,
        }}
      >
        {integration.description}
      </p>

      <span
        style={{
          fontSize: 11,
          color: "var(--muted-foreground)",
          fontWeight: "var(--font-weight-semibold)",
        }}
      >
        View details →
      </span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Detail panel
// ═══════════════════════════════════════════════════════════════════════════

interface IntegrationDetailPanelProps {
  integration: Integration;
  onClose: () => void;
  onOpenAIChat: (topic?: string) => void;
  activeBrandName?: string;
}

function IntegrationDetailPanel({
  integration,
  onClose,
  onOpenAIChat,
  activeBrandName,
}: IntegrationDetailPanelProps) {
  const IconComponent = integration.icon;
  const statusConfig = INTEGRATION_STATUS_CONFIG[integration.status];

  return (
    <>
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
        style={{
          backgroundColor: "rgba(0,0,0,0.5)",
          backdropFilter: "blur(4px)",
        }}
      />

      <div
        className="fixed right-0 top-0 bottom-0 z-50 overflow-y-auto shadow-2xl"
        style={{
          width: "min(720px, 100vw)",
          backgroundColor: "var(--background)",
          borderLeft: "1px solid var(--border)",
          animation: "slideInRight 0.3s ease-out",
        }}
      >
        <div
          className="sticky top-0 z-10 px-5 md:px-8 py-5 md:py-6 border-b"
          style={{
            backgroundColor: "var(--background)",
            borderColor: "var(--border)",
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={onClose}
              className="flex items-center gap-2 transition-colors"
              style={{
                color: "var(--muted-foreground)",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "var(--text-sm)",
                fontWeight: "var(--font-weight-medium)",
              }}
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <button
              onClick={onClose}
              aria-label="Close"
              className="p-2 rounded-lg transition-colors hover:bg-muted"
              style={{
                color: "var(--muted-foreground)",
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-start gap-3 md:gap-4">
            <div
              className="w-12 h-12 md:w-16 md:h-16 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{
                backgroundColor: "var(--muted)",
                border: "1px solid var(--border)",
              }}
            >
              <IconComponent
                className="w-6 h-6 md:w-8 md:h-8"
                style={{ color: "var(--foreground)" }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <h2
                style={{
                  fontSize: "clamp(18px, 3.5vw, var(--text-xl, 20px))",
                  fontWeight: "var(--font-weight-semibold)",
                  color: "var(--foreground)",
                  marginBottom: 6,
                  lineHeight: 1.3,
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                {integration.name}
              </h2>
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className="px-2 md:px-3 py-1 rounded flex items-center gap-1.5"
                  style={{
                    backgroundColor: statusConfig.bgColor,
                    color: statusConfig.color,
                    fontSize: "var(--text-xs)",
                    fontWeight: "var(--font-weight-semibold)",
                    border: `1px solid ${statusConfig.borderColor}`,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  {statusConfig.label}
                </span>
                <span
                  style={{
                    fontSize: "var(--text-xs)",
                    color: "var(--muted-foreground)",
                  }}
                >
                  {INTEGRATION_CATEGORIES[integration.category]}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="px-5 md:px-8 py-6 space-y-6 md:space-y-8">
          <div>
            <h3
              style={{
                fontSize: "var(--text-lg)",
                fontWeight: "var(--font-weight-semibold)",
                color: "var(--foreground)",
                marginBottom: 10,
              }}
            >
              What this integration does
            </h3>
            <p
              style={{
                fontSize: "var(--text-base)",
                color: "var(--foreground)",
                lineHeight: 1.6,
              }}
            >
              {integration.detailDescription}
            </p>
          </div>

          {/*
            PUBLISHING DESTINATIONS: live connection status + connect flow.
            Non-publishing: a roadmap/coming-soon status blurb.
          */}
          {integration.isPublishingDestination &&
            integration.publishingDestination && (
              <PublishingIntegrationStatus
                destination={integration.publishingDestination}
                brandName={activeBrandName}
              />
            )}

          {/* The two read-only Google connectors: live status, and for admins
              the connect flow. Non-admins see status only. */}
          {integration.googleConnector && (
            <GoogleConnectorStatus
              provider={integration.googleConnector}
              brandName={activeBrandName}
            />
          )}

          <div>
            <h3
              style={{
                fontSize: "var(--text-lg)",
                fontWeight: "var(--font-weight-semibold)",
                color: "var(--foreground)",
                marginBottom: 10,
              }}
            >
              Data Flow
            </h3>
            <div className="space-y-3 md:space-y-4">
              <div
                className="p-4 rounded-lg"
                style={{ backgroundColor: "var(--muted)" }}
              >
                <h4
                  style={{
                    fontSize: "var(--text-sm)",
                    fontWeight: "var(--font-weight-semibold)",
                    color: "var(--foreground)",
                    marginBottom: 8,
                  }}
                >
                  {integration.googleConnector
                    ? "What Upmode reads"
                    : "From AI Platform"}
                </h4>
                <ul className="space-y-2">
                  {(integration.googleConnector
                    ? integration.dataFlow.to
                    : integration.dataFlow.from ?? []
                  ).map((item, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span
                        style={{ color: "var(--accent)", marginTop: 4 }}
                      >
                        •
                      </span>
                      <span
                        style={{
                          fontSize: "var(--text-sm)",
                          color: "var(--muted-foreground)",
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                        }}
                      >
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div
                className="p-4 rounded-lg"
                style={{ backgroundColor: "var(--muted)" }}
              >
                <h4
                  style={{
                    fontSize: "var(--text-sm)",
                    fontWeight: "var(--font-weight-semibold)",
                    color: "var(--foreground)",
                    marginBottom: 8,
                  }}
                >
                  {integration.googleConnector
                    ? "What Upmode never does"
                    : `To ${integration.name}`}
                </h4>
                <ul className="space-y-2">
                  {(integration.googleConnector
                    ? GOOGLE_NEVER_DOES
                    : integration.dataFlow.to
                  ).map((item, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span
                        style={{ color: "var(--accent)", marginTop: 4 }}
                      >
                        •
                      </span>
                      <span
                        style={{
                          fontSize: "var(--text-sm)",
                          color: "var(--muted-foreground)",
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                        }}
                      >
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div>
            <h3
              style={{
                fontSize: "var(--text-lg)",
                fontWeight: "var(--font-weight-semibold)",
                color: "var(--foreground)",
                marginBottom: 10,
              }}
            >
              Value to You
            </h3>
            <ul className="space-y-3">
              {integration.benefits.map((benefit, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle2
                    className="w-5 h-5 flex-shrink-0 mt-0.5"
                    style={{ color: "var(--success-text)" }}
                  />
                  <span
                    style={{
                      fontSize: "var(--text-base)",
                      color: "var(--foreground)",
                      lineHeight: 1.6,
                    }}
                  >
                    {benefit}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {!integration.isPublishingDestination && !integration.googleConnector && (
            <div
              className="p-5 md:p-6 rounded-lg"
              style={{
                backgroundColor: statusConfig.bgColor,
                border: `1px solid ${statusConfig.borderColor}`,
              }}
            >
              <h3
                style={{
                  fontSize: "var(--text-lg)",
                  fontWeight: "var(--font-weight-semibold)",
                  color: statusConfig.color,
                  marginBottom: 10,
                }}
              >
                Status & Setup
              </h3>
              <p
                style={{
                  fontSize: "var(--text-base)",
                  color: statusConfig.color,
                  lineHeight: 1.6,
                }}
              >
                {integration.statusNote}
              </p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              onClick={() =>
                onOpenAIChat(
                  `Tell me more about the ${integration.name} integration. How can I use it to improve my AI brand performance?`
                )
              }
              className="flex-1"
              icon={Sparkles}
            >Ask AI About This Integration
            </Button>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
      `}</style>
    </>
  );
}